import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API, cn, DEPARTMENT_LABELS, DEPARTMENT_COLORS, STATUS_COLORS, STATUS_LABELS, formatDate, isOverdue, getDaysUntil, isHoD, canReassignTasks } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Skeleton } from "../components/ui/skeleton";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Users, 
  CheckSquare, 
  ArrowRight, 
  Loader2, 
  AlertCircle,
  Calendar,
  Package,
  User,
  Filter,
  RefreshCw
} from "lucide-react";

export default function TaskDelegationPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [filterStatus, setFilterStatus] = useState("pending");

  const userRole = user?.role;
  const canDelegate = canReassignTasks(userRole);

  useEffect(() => {
    if (canDelegate) {
      fetchData();
    }
  }, [canDelegate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch tasks assigned to current user (HoD's tasks to delegate)
      const [tasksRes, teamRes] = await Promise.all([
        axios.get(`${API}/tasks/my-tasks`, { withCredentials: true }),
        axios.get(`${API}/users/my-team`, { withCredentials: true }),
      ]);
      
      setTasks(tasksRes.data);
      setTeamMembers(teamRes.data);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      toast.error("Failed to load delegation data");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTask = (taskId) => {
    setSelectedTasks((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleSelectAll = () => {
    const filteredTasks = getFilteredTasks();
    if (selectedTasks.length === filteredTasks.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(filteredTasks.map((t) => t.task_id));
    }
  };

  const handleAssignTasks = async () => {
    if (!selectedMember || selectedTasks.length === 0) {
      toast.error("Please select tasks and a team member");
      return;
    }

    setAssigning(true);
    try {
      // Reassign each task
      await Promise.all(
        selectedTasks.map((taskId) =>
          axios.post(
            `${API}/tasks/${taskId}/reassign`,
            { assigned_to: selectedMember },
            { withCredentials: true }
          )
        )
      );

      const member = teamMembers.find((m) => m.user_id === selectedMember);
      toast.success(`${selectedTasks.length} task(s) assigned to ${member?.name}`);
      
      // Reset selection and refresh
      setSelectedTasks([]);
      setSelectedMember(null);
      setConfirmDialog(false);
      fetchData();
    } catch (err) {
      console.error("Failed to assign tasks:", err);
      toast.error(err.response?.data?.detail || "Failed to assign tasks");
    } finally {
      setAssigning(false);
    }
  };

  const getFilteredTasks = () => {
    if (filterStatus === "all") return tasks;
    return tasks.filter((t) => t.status === filterStatus);
  };

  const filteredTasks = getFilteredTasks();
  const pendingCount = tasks.filter((t) => t.status === "pending").length;
  const inProgressCount = tasks.filter((t) => t.status === "in_progress").length;

  if (!canDelegate) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <AlertCircle className="w-12 h-12 text-muted-foreground mb-4" />
        <p className="text-lg font-medium">Access Restricted</p>
        <p className="text-muted-foreground">Only Admin and Head of Department can delegate tasks.</p>
      </div>
    );
  }

  if (loading) {
    return <TaskDelegationSkeleton />;
  }

  return (
    <div className="space-y-6" data-testid="task-delegation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Task Delegation</h1>
          <p className="text-muted-foreground mt-1">
            Assign tasks to your team members
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          className="rounded-sm"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border border-border shadow-none rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-50 rounded-sm">
                <CheckSquare className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-heading">{pendingCount}</p>
                <p className="text-xs text-muted-foreground">Pending Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border shadow-none rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-sm">
                <Loader2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-heading">{inProgressCount}</p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border shadow-none rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-50 rounded-sm">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-heading">{teamMembers.length}</p>
                <p className="text-xs text-muted-foreground">Team Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border shadow-none rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-50 rounded-sm">
                <CheckSquare className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-heading">{selectedTasks.length}</p>
                <p className="text-xs text-muted-foreground">Selected</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tasks List */}
        <div className="lg:col-span-2">
          <Card className="border border-border shadow-none rounded-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-xl">My Tasks to Delegate</CardTitle>
                  <CardDescription>{filteredTasks.length} tasks</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[140px] rounded-sm h-9">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredTasks.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No tasks to delegate</p>
                  <p className="text-sm">All tasks have been assigned or completed</p>
                </div>
              ) : (
                <>
                  {/* Select All */}
                  <div className="flex items-center gap-2 pb-3 border-b mb-3">
                    <Checkbox
                      checked={selectedTasks.length === filteredTasks.length && filteredTasks.length > 0}
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                    <span className="text-sm text-muted-foreground">
                      Select all ({filteredTasks.length})
                    </span>
                  </div>

                  <ScrollArea className="h-[500px] pr-4">
                    <div className="space-y-2">
                      {filteredTasks.map((task) => {
                        const daysUntil = getDaysUntil(task.target_date);
                        const overdue = isOverdue(task.target_date);
                        const deptColors = DEPARTMENT_COLORS[task.department] || DEPARTMENT_COLORS.merchandizing;
                        const statusColors = STATUS_COLORS[task.status] || STATUS_COLORS.pending;

                        return (
                          <div
                            key={task.task_id}
                            className={cn(
                              "flex items-start gap-3 p-3 rounded-sm border transition-colors cursor-pointer",
                              selectedTasks.includes(task.task_id)
                                ? "border-primary bg-primary/5"
                                : "border-border hover:bg-muted/30"
                            )}
                            onClick={() => handleSelectTask(task.task_id)}
                            data-testid={`task-item-${task.task_id}`}
                          >
                            <Checkbox
                              checked={selectedTasks.includes(task.task_id)}
                              onCheckedChange={() => handleSelectTask(task.task_id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <p className="font-medium text-sm">{task.task_name}</p>
                                  <p className="text-xs text-muted-foreground font-mono">
                                    {task.order_number} • {task.item_name}
                                  </p>
                                </div>
                                <Badge variant="secondary" className={cn("text-xs shrink-0", statusColors.light)}>
                                  {STATUS_LABELS[task.status]}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="outline" className={cn("text-xs capitalize", deptColors.light)}>
                                  {DEPARTMENT_LABELS[task.department]}
                                </Badge>
                                <span className={cn(
                                  "text-xs flex items-center gap-1",
                                  overdue ? "text-destructive" : "text-muted-foreground"
                                )}>
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(task.target_date)}
                                  {overdue && " (Overdue)"}
                                  {!overdue && daysUntil !== null && daysUntil <= 3 && ` (${daysUntil}d)`}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Team Members Panel */}
        <div>
          <Card className="border border-border shadow-none rounded-sm sticky top-6">
            <CardHeader>
              <CardTitle className="font-heading text-xl">Assign To</CardTitle>
              <CardDescription>Select a team member</CardDescription>
            </CardHeader>
            <CardContent>
              {teamMembers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No team members found</p>
                  <p className="text-xs">Add team members to your department first</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {teamMembers.map((member) => (
                    <div
                      key={member.user_id}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors",
                        selectedMember === member.user_id
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/30"
                      )}
                      onClick={() => setSelectedMember(member.user_id)}
                      data-testid={`team-member-${member.user_id}`}
                    >
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={member.picture} />
                        <AvatarFallback className="text-xs">
                          {member.name?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{member.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                      </div>
                      {selectedMember === member.user_id && (
                        <div className="w-2 h-2 rounded-full bg-primary" />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Assign Button */}
              <div className="mt-6 pt-4 border-t">
                <Button
                  className="w-full rounded-sm"
                  disabled={selectedTasks.length === 0 || !selectedMember}
                  onClick={() => setConfirmDialog(true)}
                  data-testid="assign-tasks-btn"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Assign {selectedTasks.length} Task{selectedTasks.length !== 1 ? "s" : ""}
                </Button>
                {selectedTasks.length > 0 && !selectedMember && (
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    Select a team member to assign
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Task Assignment</DialogTitle>
            <DialogDescription>
              You are about to assign {selectedTasks.length} task(s) to{" "}
              <strong>{teamMembers.find((m) => m.user_id === selectedMember)?.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-2">Tasks to assign:</p>
            <ScrollArea className="h-[150px]">
              <ul className="space-y-1">
                {selectedTasks.map((taskId) => {
                  const task = tasks.find((t) => t.task_id === taskId);
                  return (
                    <li key={taskId} className="text-sm flex items-center gap-2">
                      <CheckSquare className="w-3 h-3 text-muted-foreground" />
                      {task?.task_name}
                    </li>
                  );
                })}
              </ul>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignTasks} disabled={assigning} data-testid="confirm-assign-btn">
              {assigning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm Assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TaskDelegationSkeleton = () => (
  <div className="space-y-6">
    <div>
      <Skeleton className="h-9 w-48" />
      <Skeleton className="h-4 w-64 mt-2" />
    </div>
    <div className="grid grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <Skeleton key={i} className="h-20" />
      ))}
    </div>
    <div className="grid grid-cols-3 gap-6">
      <div className="col-span-2">
        <Skeleton className="h-[600px]" />
      </div>
      <Skeleton className="h-[400px]" />
    </div>
  </div>
);
